// Shared helpers for the source-citation UI. Kept in one place so the favicon provider, the
// URL-safety rules and the title sanitization can't drift between the three paths that render
// index-sourced citations: the inline chip (SourceChip), the expandable dropdown (SourcesDropdown)
// and the legacy "References" footer (ChatHistorySourceLinks). Switching the favicon provider,
// tightening the scheme allowlist, or hardening the sanitizer is now a single-site change.

import DOMPurify from 'dompurify';

// Citation titles come verbatim from the content-search index (no escaping), so render them as
// sanitized text — strip every tag and attribute. Titles are plain article names; this only decodes
// entities (e.g. `&amp;` → `&`) and neutralizes any markup, closing the dangerouslySetInnerHTML XSS
// vector (#1918). Every title sink — chip popover, dropdown and the legacy footer — routes through here.
export function sanitizeTitle(title: string | undefined): string {
    return title ? DOMPurify.sanitize(title, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }) : '';
}

/** Parse the hostname out of a source URL; fall back to the raw string if it doesn't parse. */
export function getHostname(url: string): string {
    try {
        return new URL(url).hostname;
    } catch {
        return url;
    }
}

/** Favicon lookup via Google's public favicon service. */
export function getFaviconUrl(hostname: string): string {
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=32`;
}

// The url comes from the same untrusted content-search index as the title, so only render it as a
// link when it resolves to a safe target. Absolute http(s) links (help-center pages) are allowed;
// anything else must be a same-origin relative path. Resolving against the page origin collapses
// protocol-relative tricks (`//host`, and `/\host` which browsers normalize to `//host`) to their
// real external origin, which then fails the same-origin check — so they can't masquerade as a
// relative path and open-redirect/phish. Unsafe schemes (`javascript:`, `data:`) never match either
// branch. React does not block these hrefs at runtime, so this guard is the only gate (the same
// trust boundary #1918/#1921 close, now shared by the chips, dropdown and legacy footer). Unsafe
// URLs render as plain text, not a link.
export function isSafeUrl(url: string | undefined): url is string {
    if (!url) {
        return false;
    }
    if (/^https?:\/\//i.test(url)) {
        return true;
    }
    try {
        const resolved = new URL(url, window.location.origin);
        return (
            (resolved.protocol === 'http:' || resolved.protocol === 'https:') &&
            resolved.origin === window.location.origin
        );
    } catch {
        return false;
    }
}
